import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import {
  Flex,
  Box,
  Divider,
  UseDisclosureProps,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { customReportActions } from '@/store/custom-report';
import { staticReportActions } from '@/store/static-report';
import HeaderActions from '@/pages/reports/custom-reports/show/headerActions';
import { TooltipText } from '@/ui/TooltipText';
import { KText } from '@/ui-library/components';
import { Link } from 'react-router-dom';
import { Edit05, LineChartUp03 } from '@kula-ai/icons-core';
import { LastRefreshedTime } from './components';
import { FiltersWrapper } from '@/pages/reports/custom-reports/show/FiltersWrapper';
import { ReportTypes } from '../../types';
import { REPORT_CONSTANTS } from '../../constants';
import { CreateEditCustomReportModal } from '@/pages/reports/custom-reports/components/CreateEditCustomReportModal';
import { useQueryClient } from '@tanstack/react-query';
import {
  getItem,
  setItem,
  useIsPreferencesLoaded,
} from '@/store/ServerStorage/serverStorage';
import { STORAGE_KEYS } from '@/utils/storageKeys';

interface ReportHeaderProps {
  reportData: ReportTypes.ReportMeta;
  isEditMode: boolean;
  hasOriginalTiles: boolean;
  setEditMode: (_isEditMode: boolean) => void;
  showAddBlockDisclosure: UseDisclosureProps;
  onFilterChange: (_filterType: string, _filterData: unknown) => void;
}

export const ReportHeader: FC<ReportHeaderProps> = React.memo(
  ({
    reportData,
    isEditMode,
    setEditMode,
    hasOriginalTiles,
    showAddBlockDisclosure,
    onFilterChange,
  }) => {
    const [localTitle, setLocalTitle] = useState<string>('');
    const dispatch = useDispatch();
    const {
      layoutData: report,
      defaultFilters,
      title,
      type,
      filtersToShow,
      isOwner,
      canModifyBlocks,
      isCustomReport,
    } = reportData;

    const queryClient = useQueryClient();
    const isPreferencesLoaded = useIsPreferencesLoaded();

    const isStaticReport = type === 'static';
    // Match FiltersWrapper's pattern: dispatch into the slice the tile fetch
    // actually reads from, so saved-view rehydrate works for static reports too.
    const { applyFilter, applyTimeFrame, applyViewBy } = isStaticReport
      ? staticReportActions
      : customReportActions;

    // Filter state managed locally in header
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [isLocalFilterApplied, setIsLocalFilterApplied] = useState(false);

    const isLocalFilterAppliedRef = useRef(false);
    isLocalFilterAppliedRef.current = isLocalFilterApplied;

    const handleSetSelectedFilters = useCallback(
      (filters: unknown[]) => {
        setSelectedFilters(filters);
        if (!isEditMode && isLocalFilterAppliedRef.current) {
          const storageKey = STORAGE_KEYS.getReportFiltersKey(
            reportData?.reportId
          );
          if (!storageKey) return;
          if (isStaticReport) {
            // Static report defaults are hardcoded and never change, so the
            // local override is always applied if present.
            setItem(storageKey, filters);
          } else {
            // Custom reports: store the override with a snapshot of the API
            // filters at this moment. Rehydrate compares the snapshot to the
            // current API filters — a mismatch means the report changed
            // externally (e.g. owner saved new defaults) and we discard the
            // stale override.
            setItem(storageKey, {
              localFilters: filters,
              baselineSnapshot: defaultFilters?.normalFilters ?? [],
            });
          }
        }
      },
      [isEditMode, reportData?.reportId, isStaticReport, defaultFilters]
    );

    const showUpdateReportModal = useDisclosure();

    const reportTitle =
      localTitle || report?.name || report?.title || title || '';
    const showEditIcon = isOwner && reportTitle && isCustomReport; // Only show edit for custom reports

    const reportTitleRef = useRef(reportTitle);
    reportTitleRef.current = reportTitle;
    const reportRef = useRef(report);
    reportRef.current = report;
    const reportIdRef = useRef(reportData?.reportId);
    reportIdRef.current = reportData?.reportId;

    // Only run on actual unmount - no dependencies
    useEffect(() => {
      return () => {
        const reportId = reportIdRef.current;
        const latestReport = reportRef.current;
        const latestTitle = reportTitleRef.current;

        // on unmount, update the report name in the cache with latest one
        if (reportId && latestReport) {
          queryClient.setQueryData([`getCustomReports/${reportId}`], {
            data: { ...latestReport, name: latestTitle },
            status: 'success',
          });
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Derive local title: once report data arrives and no user edit has occurred,
    // sync it during render instead of via useEffect (avoids an extra render cycle).
    if (report?.name && !localTitle) {
      setLocalTitle(report.name);
    }

    // Listen for title updates from the modal
    useEffect(() => {
      const handleTitleUpdate = (event: CustomEvent) => {
        setLocalTitle(event.detail.newTitle);
      };

      const reportContainer = document.querySelector('[data-report-container]');
      if (reportContainer) {
        reportContainer.addEventListener(
          'title-updated',
          handleTitleUpdate as EventListener
        );
        return () => {
          reportContainer.removeEventListener(
            'title-updated',
            handleTitleUpdate as EventListener
          );
        };
      }
    }, []);

    // Rehydrate the per-user local override once preferences are loaded and
    // FiltersWrapper has finished its own initialisation. Skip in edit mode —
    // the L193 reset effect owns filter state when editing defaults.
    //
    // Static reports: stored shape is the raw filters array; always apply.
    // Custom reports: stored shape is `{ localFilters, baselineSnapshot }`.
    //   Apply only when the snapshot still matches the current API filters;
    //   otherwise the report changed externally (e.g. owner saved new
    //   defaults) and we discard the stale override.
    useEffect(() => {
      const storageKey = STORAGE_KEYS.getReportFiltersKey(reportData?.reportId);
      if (!isPreferencesLoaded || !storageKey || !isLocalFilterApplied) return;
      if (isEditMode) return;
      const stored = getItem(storageKey);
      if (!stored) return;

      let localFilters: unknown[] | undefined;

      if (isStaticReport) {
        if (!Array.isArray(stored)) return;
        localFilters = stored;
      } else {
        // Legacy shape (raw array) from earlier versions — discard.
        if (Array.isArray(stored)) {
          setItem(storageKey, null);
          return;
        }
        const { localFilters: stashedLocal, baselineSnapshot } = stored as {
          localFilters?: unknown[];
          baselineSnapshot?: unknown[];
        };
        const apiBaseline = defaultFilters?.normalFilters ?? [];
        const baselineMatches =
          JSON.stringify(baselineSnapshot ?? []) ===
          JSON.stringify(apiBaseline);
        if (!baselineMatches) {
          setItem(storageKey, null);
          return;
        }
        localFilters = stashedLocal;
      }

      if (Array.isArray(localFilters) && localFilters.length > 0) {
        setSelectedFilters(localFilters);
        const filterData = localFilters.reduce(
          (acc, { filter_by, filter_condition, filter_by_values }) => {
            if (filter_by_values?.length) {
              acc[filter_by.value] = {
                [filter_condition.value]: filter_by_values.map(({ id }) => id),
              };
            }
            return acc;
          },
          {} as Record<string, Record<string, unknown[]>>
        );
        dispatch(applyFilter(filterData));
      }
    }, [
      isStaticReport,
      isPreferencesLoaded,
      reportData?.reportId,
      isLocalFilterApplied,
      isEditMode,
      dispatch,
      applyFilter,
      defaultFilters,
    ]);

    // Reset filters to defaultFilters when entering edit mode
    useEffect(() => {
      if (isEditMode && defaultFilters) {
        // Reset normal filters if different
        if (defaultFilters.normalFilters) {
          const currentFiltersString = JSON.stringify(selectedFilters);
          const defaultFiltersString = JSON.stringify(
            defaultFilters.normalFilters
          );

          if (currentFiltersString !== defaultFiltersString) {
            setSelectedFilters(defaultFilters.normalFilters);
          }
        }

        // Reset timeframe if different
        if (defaultFilters.timeframe) {
          dispatch(applyTimeFrame({ ...defaultFilters.timeframe }));
        }

        // Reset dateGroup if different
        if (defaultFilters.dateGroup) {
          const dateGroupId =
            typeof defaultFilters.dateGroup === 'object'
              ? defaultFilters.dateGroup.id
              : defaultFilters.dateGroup;

          if (dateGroupId) {
            dispatch(applyViewBy({ id: dateGroupId }));
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, defaultFilters, dispatch, applyTimeFrame, applyViewBy]);

    const renderBreadcrumb = () => {
      return (
        <Link
          to={REPORT_CONSTANTS.COPY_CHANGES.BREADCRUMB_TO}
          style={{
            display: 'inline-block',
            width: 'fit-content',
            height: '20px',
          }}
        >
          <Flex alignItems="center" gap="6px" w="fit-content">
            <LineChartUp03 size={14} />
            <KText size="sm-regular" color="secondary-700" h="20px">
              {REPORT_CONSTANTS.COPY_CHANGES.BREADCRUMB_PATH}
            </KText>
          </Flex>
        </Link>
      );
    };

    const renderTitle = () => {
      return (
        <>
          <TooltipText
            stringLength={50}
            text={reportTitle}
            as="span"
            size="xl-medium"
            h="24px"
            color="secondary-900"
            variant="default"
          />
          {showEditIcon && (
            <Edit05
              size={16}
              className="!text-primary-900 cursor-pointer mt-0.5"
              onClick={showUpdateReportModal.onOpen}
            />
          )}
        </>
      );
    };

    const renderTags = () => {
      return (
        <Flex alignItems="center" gap="6px" pl="6px">
          {hasOriginalTiles && <LastRefreshedTime scope="reports" />}
          {isEditMode && (
            <Flex
              bg="pastel-6-light"
              p="4px 8px"
              alignItems="center"
              gap="6px"
              borderRadius="sm"
              flexShrink={0}
              w="fit-content"
              h="26px"
            >
              {/* <Edit05 size={16} className="!text-pastel-6dark" /> */}
              <KText size="sm-medium" color="pastel-6-dark" h="20px">
                You're in edit mode
              </KText>
            </Flex>
          )}
        </Flex>
      );
    };

    const getReportTypeForFilters = () => {
      const titleToReportType = {
        'Pipeline history': 'pipeline',
        Interviews: 'interviews',
        Offers: 'offers',
        Hires: 'hires',
        Rejections: 'rejections',
        Contacts: 'contacts',
        Jobs: 'jobs',
        EEO: 'eeo',
        'Recruiter performance': 'recruiter-performance',
        'Flow Health': 'flow-health',
        Requisitions: 'requisitions',
        'Current job pipeline': 'live_job_pipeline',
      };
      return titleToReportType[title] || 'pipeline';
    };

    return (
      <Box w="100%">
        {/* Header Section */}
        <Flex
          borderBottomWidth="1px"
          borderBottomColor="border-color"
          boxShadow="ats-shadow-3"
          justifyContent="center"
          p="12px 24px"
          w="100%"
          gap="3px"
          flexDir="column"
        >
          {/* Breadcrumb */}
          {renderBreadcrumb()}
          {/* Title, edit icon, last refreshed time and actions */}
          <Flex alignItems="center" justifyContent="space-between" w="100%">
            {/* left part */}
            <Flex alignItems="baseline" gap="6px" flex={1}>
              {renderTitle()}
              {renderTags()}
            </Flex>
            {/* right part */}
            {report?.id && (
              <HeaderActions
                customReport={report}
                initiateTileSelection={() => {
                  showAddBlockDisclosure?.onOpen();
                }}
                selectedFilters={selectedFilters}
                hasWriteAccess={canModifyBlocks}
                isOwner={isOwner}
                isStaticReport={isStaticReport}
                isEditMode={isEditMode}
                setEditMode={setEditMode}
              />
            )}
          </Flex>

          <Divider my="6px" />

          <FiltersWrapper
            selectedFilters={selectedFilters}
            setSelectedFilters={handleSetSelectedFilters}
            filtersToShow={filtersToShow as string[]}
            isLocalFilterApplied={isLocalFilterApplied}
            setIsLocalFilterApplied={setIsLocalFilterApplied}
            isStaticReport={isStaticReport}
            reportType={getReportTypeForFilters()}
            onFilterChange={isEditMode ? onFilterChange : undefined}
            defaultFilters={defaultFilters}
          />
        </Flex>

        {showUpdateReportModal.isOpen && (
          <CreateEditCustomReportModal
            type="edit"
            report={{ ...report, name: reportTitle }}
            onClose={showUpdateReportModal.onClose}
            submitButtonLabel="Update report"
            modalTitle={{
              headerText: 'Name of custom report',
              subHeaderText: `Give your report a unique name so it's easier to identify`,
            }}
            onSubmit={() => {
              showUpdateReportModal.onClose();
            }}
          />
        )}
      </Box>
    );
  }
);

ReportHeader.displayName = 'ReportHeader';
